import { Component, OnInit, OnDestroy, Input, NgZone, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ToastService } from '../../../services/toast.service';
import { ReportService } from '../../../services/report.service';
import { Report } from '../../../models/report';
import { Server } from '../../../models/server';
import { DestinationEndpoint } from '../../../models/destination-endpoint';
import { ImportType } from '../../../models/import-type';
import { DataSourceType } from '../../../models/data-source-type';
import { IntegrationRunTime } from '../../../models/integration-runtime';
import { SearchTerm } from '../../../models/search-term';
import { Integration } from '../../../models/integration';
import { takeUntil } from 'rxjs/operators';
import { IntegrationJavaScript } from 'src/app/models/integration-javascript';
import { LOSTalkerTitleService } from '../../../services/lostalker-title-service.service';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';

@Component({
  selector: 'app-integration-report-view',
  templateUrl: './integration-report-view.component.html',
  styleUrls: ['./integration-report-view.component.scss'],
  providers: [ClientIntegrationService, ReportService, ToastService, LOSTalkerTitleService],
  standalone: false,
})
export class IntegrationReportViewComponent implements OnInit, OnDestroy {
  componentDestroyed$ = new Subject<boolean>();
  @Input() reportId = '';
  @Input() currentReportToShow: Report;
  currReport: Report;

  integrationGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  filtersChanged = false;
  xmlSearchTerm: SearchTerm;
  jsSearchTerm: SearchTerm;

  integrations: Integration[] = [];
  filteredIntegrations: Integration[] = [];

  // in report
  useJSSearch = false;
  jsLoaded = false;

  @Input() serverItems: Server[] = [];
  @Input() dataSourceTypeItems: DataSourceType[] = [];
  @Input() destinationEndpointItems: DestinationEndpoint[] = [];
  @Input() importTypeItems: ImportType[] = [];
  @Input() integrationRunTimeItems: IntegrationRunTime[] = [];
  javascripts: IntegrationJavaScript[] = [];

  serverItemsSelected: Server[] = [];
  dataSourceItemsSelected: DataSourceType[] = []; // Dynamic CSV ...
  destinationEndpointItemsSelected: DestinationEndpoint[] = []; // csd.. lg..
  importTypeItemsSelected: ImportType[] = [];

  constructor(
    protected clientService: ClientIntegrationService,
    protected reportService: ReportService,
    protected toastService: ToastService,
    private location: Location,
    private router: Router,
    private titleService: LOSTalkerTitleService,
    private ngZone: NgZone,
  ) {
    this.xmlSearchTerm = new SearchTerm('xmlConfig', 'Search XML Config...');
    this.jsSearchTerm = new SearchTerm('', 'Search JS...');
  }

  ngOnInit(): void {
    if (this.currentReportToShow) {
      this.currReport = new Report(this.currentReportToShow);
      this.integrationGridSettings.set(this.setupGrid());
      this.titleService.setUpTitle('Report - ' + this.currentReportToShow.title);

      this.getIntegrations();
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getIntegrations(): void {
    this.integrationGridSettings().loading.set(true);
    if (this.currReport.dataSproc !== '' && this.currReport.dataSproc !== undefined) {
      if (this.useJSSearch) {
        this.jsLoaded = true;
        this.reportService.getIntegrationTypeQueryDataWithJS(
          this.currReport.reportId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(
            (data) => {
              this.integrations = data;

              if (this.integrationRunTimeItems.length > 0 && this.integrations.length > 0) {
                this.integrations.forEach(i => {
                  i.integrationRunTimes = this.integrationRunTimeItems.filter(x => x.integrationId == i.integrationId);
                });
              }

              this.updateFilteredData();
            },
            (_error) => {
              this.integrationGridSettings().loading.set(false);
            });
      }
      else {
        this.reportService.getIntegrationTypeQueryData(
          this.currReport.reportId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(
            (data) => {
              this.integrations = data;

              if (this.integrationRunTimeItems.length > 0 && this.integrations.length > 0) {
                this.integrations.forEach(i => {
                  i.integrationRunTimes = this.integrationRunTimeItems.filter(x => x.integrationId == i.integrationId);
                });
              }

              this.updateFilteredData();
            },
            (_error) => {
              this.integrationGridSettings().loading.set(false);
            });
      }
    }
  }

  clearSearch(item: SearchTerm) {
    item.clear();
    this.updateFilteredData();
  }

  updateSearch(item: SearchTerm) {
    item.save();
    this.updateFilteredData();
  }
  
  loadJS() {
    this.jsLoaded = true;
    this.integrationGridSettings().loading.set(true);
    this.clientService.getAllIntegrationJavaScripts().subscribe(data => {
      this.javascripts = data;
      this.integrations.forEach(i => {
        const js = this.javascripts.filter(j => j.integrationId == i.integrationId);
        if(js != null)
        {
          i.javaScript = js;
        }
      });
      this.updateFilteredData();
    });
  }

  // update filters without the column filters
  public updateFilteredData() {
    this.filteredIntegrations = [];
    this.filteredIntegrations = this.integrations;

    // filter xml config by Search Term
    if (this.currReport.hasSearch && this.xmlSearchTerm.hasSearchTerm) {
      this.filteredIntegrations = this.filteredIntegrations.filter(integration => integration.xmlConfig?.toLowerCase()?.includes(this.xmlSearchTerm?.currentSearchValue?.toLowerCase()));
    }
    
    // filter js config by Search Term
    if (this.jsLoaded && this.currReport.hasSearch && this.jsSearchTerm.hasSearchTerm) {
      this.filteredIntegrations = this.filteredIntegrations.filter(x => x.javaScript.some(y => y.javaScript.toLowerCase().includes(this.jsSearchTerm.currentSearchValue.toLowerCase())));
    }

    // filter by Integration Filters
    if (this.currReport.hasFilterCards) {
      // data source
      
      if (this.dataSourceItemsSelected.length > 0) {
        const dataSourceIds = this.dataSourceItemsSelected.map(i => i.dataSourceTypeId);
        this.filteredIntegrations =
          this.filteredIntegrations.filter(i => dataSourceIds.indexOf(i.dataSourceTypeId) != -1);
      }

      // RunTime
      if (this.integrationRunTimeItems.length > 0) {
        // Destination Endpoint
        if (this.destinationEndpointItemsSelected.length > 0
          && this.destinationEndpointItemsSelected.length != this.destinationEndpointItems.length) {
            const dests = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
            this.filteredIntegrations = this.filteredIntegrations.filter(i => i.integrationRunTimes != null);
            this.filteredIntegrations = this.filteredIntegrations.filter(x => x.integrationRunTimes?.filter(y => dests.some(z => y.destinationEndpointId == z)).length != 0);
        }
        // Import Type
        if (this.importTypeItemsSelected.length > 0
          && this.importTypeItemsSelected.length != this.importTypeItems.length) {
            const imTypes = this.importTypeItemsSelected.map(i => i.importTypeId);
            this.filteredIntegrations = this.filteredIntegrations.filter(i => i.integrationRunTimes != null);
            this.filteredIntegrations = this.filteredIntegrations.filter(x => x.integrationRunTimes?.filter(y => imTypes.some(z => y.importTypeId == z)).length != 0);
        }
      }
      // server
      if (this.serverItemsSelected.length > 0) {
        const serverIds = this.serverItemsSelected.map(i => i.serverId);
        this.filteredIntegrations = this.filteredIntegrations.filter(i => serverIds.indexOf(i.serverId) != -1);
      }
    }

    this.integrationGridSettings.set(this.integrationGridSettings().setData(this.filteredIntegrations));
    this.integrationGridSettings().loading.set(false);
  }

  // reset filters
  public resetFilters(): void {
    this.dataSourceItemsSelected = [];
    this.destinationEndpointItemsSelected = [];
    this.serverItemsSelected = [];
    this.importTypeItemsSelected = [];
    this.updateFilteredData();
  }
  // apply filters
  public applyFilters(): void {
    this.updateFilteredData();
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.filteredIntegrations,
      fixedGrid: true,
      columns: [
        new GridColumnData({ width: '150px', field: 'clientId', header: 'Client ID'}),
        new GridColumnData({ width: '270px', field: 'clientName', header: 'Client Name'}),
        new GridColumnData({ width: '300px', field: 'name', header: 'Integration Name'}),
        new GridColumnData({ width: '200px', field: 'dataSourceTypeId', header: 'DataSource'}),
        new GridColumnData({ width: '150px', field: 'serverId', header: 'Server'}),
        new GridColumnData({ width: '150px', field: 'locked', header: 'Locked'}),
        new GridColumnData({ width: '300px', field: 'lockReason', header: 'Lock Reason'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ filterable: false, field: 'initialCreationDate', header: 'Initial Creation Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ width: '150px', field: 'ipRestricted', header: 'IP Restricted'}),
        new GridColumnData({ width: '150px', field: 'selfHosted', header: 'Self Hosted'}),
        new GridColumnData({ field: 'integrationId', header: 'Integration Id'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: this.currReport.hasSearch,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
      actionColumn: {
        enabled: this.currReport.hasGoToButton,
        displayFirst: true,
        onSelectionChange: (event: Integration[]) => {
          const url = this.router.serializeUrl(
            this.router.createUrlTree(['/editConfigV2', event[0].clientInformationId, event[0].integrationId]),
          );
          window.open(url, '_blank');
        }, 
      },
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: this.currReport.exportFileName?.split('.')[0] ?? 'IntegrationReport',
      },
    });
  }
}
